import {HttpErrorResponse} from '@angular/common/http';
import {Component, OnDestroy, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';

import {TranslateService} from '@ngx-translate/core';

import {EMPTY, Observable, of} from 'rxjs';
import {catchError, filter, finalize, switchMap, takeUntil, tap} from 'rxjs/operators';

import isNull from 'lodash-es/isNull';
import isNumber from 'lodash-es/isNumber';

import * as moment from 'moment';

import {ToastrService} from 'ngx-toastr';
import {EntityType, TreeRootResponse} from '../../../../+organizations/org-interfaces';
import {OrgTreeService} from '../../../../+organizations/org-tree.service';
import {isNumberType} from '../../../../+reports/preview';
import {FieldZone, ReportingFields, SelectedTreeItem} from '../../../../+reports/reports.interfaces';
import {ReportsService} from '../../../../+reports/reports.service';
import {EmployeesUtilsService} from '../../../../shared/employees/employees-utils.service';
import {FieldType} from '../../../../shared/form-generator/fields/fields.interfaces';
import {Validators as GpValidators} from '../../../../shared/forms';
import {LocalizationService} from '../../../../shared/localization/localization.service';
import {ModalService} from '../../../../shared/modal';
import {Entity} from '../../../../shared/population/create-population/create-population.interfaces';
import {CreatePopulationService} from '../../../../shared/population/create-population/create-population.service';
import {DefaultPagination, Pagination, TreeNode} from '../../../../shared/ui';
import {SelectItem} from '../../../../shared/ui/select/select-item';
import {TreeItem} from '../../../../shared/ui/tree-multi-select/treeview.component';
import {ListItem, RepPopulationCriterion} from '../../user-rights.interfaces';
import {mockEntityFormDescriptors} from '../../user-rights.mocks';
import {UserRightsService} from '../../user-rights.service';
import {ManageAccessCriterionModalComponent} from '../manage-access-criterion-modal.component';
import {AccessTypes, CRITERION_PER_PAGE, STATE} from '../manage-access-criterion-modal.interface';
import {
  getEntityLazyValue,
  isBooleanType,
  isDateType,
  isEntityType,
  isListType,
  isTreeType,
  ManageAccessCriterionModalService
} from '../manage-access-criterion-modal.service';

@Component({
  selector: 'gp-manage-entity-access-criterion-modal',
  templateUrl: './manage-entity-access-criterion-modal.component.html',
  styleUrls: ['./manage-entity-access-criterion-modal.component.scss'],
})
export class ManageEntityAccessCriterionModalComponent extends ManageAccessCriterionModalComponent implements OnInit, OnDestroy {
  public manageEntityAccessCriterionForm: UntypedFormGroup;
  public entityCriterionForm: UntypedFormGroup;
  public entityTreeNameItems: ListItem[] = [];
  public entityTypeList: ListItem[] = [];
  public accessModes: ListItem[] = [];
  public selectedTreeNameItem: SelectItem = null;
  public groupZoneNameTree: Array<TreeNode<TreeItem>> = null;
  public zoneNameTree: Array<TreeNode<TreeItem>> = null;
  public criterionDataByTreeName: RepPopulationCriterion[] = [];
  public pagination: Pagination = new DefaultPagination(CRITERION_PER_PAGE);
  public selectedEntityType: SelectItem = null;
  public selectedGroupZone: SelectedTreeItem = null;
  public selectedGroupZoneName = null;
  public selectedZone: SelectedTreeItem = null;
  public selectedZoneName = null;
  public selectedZoneType = null;
  public selectedZoneMetadata = null;
  private selectedZoneTransId = null;
  public MAX_ALLOWED_LENGTH = 75;
  public lazyValue: string;
  public entityList: Entity[] = [];
  public entityList$: Observable<Entity[]>;

  public fullAccessChecked = false;
  public regulationTree: TreeItem[];
  public booleanValueChecked = false;

  private treeType = 'organization';
  private dynamicValidationFormControls = ['groupZoneName', 'zoneName', 'value'];
  public entityFormDescriptors = null;
  public entityReportingFields: FieldZone[] = [];

  constructor(
    private modalService: ModalService,
    private toastService: ToastrService,
    private translate: TranslateService,
    private formBuilder: UntypedFormBuilder,
    private userRightService: UserRightsService,
    private employeesUtilsService: EmployeesUtilsService,
    private createPopulationService: CreatePopulationService,
    private orgTreeService: OrgTreeService,
    private reportsService: ReportsService,
    private localizationService: LocalizationService,
    private manageAccessCriterionModalService: ManageAccessCriterionModalService
  ) {
    super();
    this.entityCriterionForm = this.formBuilder.group({
      fullAccess: [''],
      entityType: ['', Validators.required],
      accessMode: ['', Validators.required],
      groupZoneName: [''],
      zoneName: [''],
      value: [''],
    });

    this.manageEntityAccessCriterionForm = this.formBuilder.group({
      entityTreeName: ['', Validators.required],
    });
  }

  public get isInitialState(): boolean {
    return !this.selectedTreeNameItem && this.currentState === STATE.INITIAL_STATE;
  }

  public get isCriterionNotFound(): boolean {
    return this.selectedTreeNameItem && this.isContentLoaded && !this.isAddCriterionActive && !this.criterionDataByTreeName?.length;
  }

  public ngOnInit(): void {
    this.getEntityTreeNames();
    this.accessModes = Object.values(AccessTypes).map((type: string) => ({
      id: type,
      text: type,
    }));

    this.entityCriterionForm.get('value').valueChanges
      .pipe(
        filter(() => isEntityType(this.selectedZoneType)),
        tap((value) => this.lazyValue = getEntityLazyValue(value, this.entityList)),
        takeUntil(this.componentDestroyed$)
      ).subscribe();

    this.reportsService.getRawReportingFields()
      .pipe(
        tap((response: ReportingFields) => {
          this.entityReportingFields = response?.entity;
        }),
        takeUntil(this.componentDestroyed$)
      ).subscribe();
  }

  public ngOnDestroy(): void {
    super.ngOnDestroy();
    this.modalService.modalClosed.emit('manage-entity-access-criterion');
  }

  public getEntityFormDescriptor(): Observable<any> {
    return of(mockEntityFormDescriptors);
  }

  public showAddCriterionFormFields(): void {
    this.hideAddOrUpdateForm(this.entityCriterionForm);
    this.fullAccessChecked = false;
    this.setValidatorsAndUpdateCriterionForm();
    this.removeSelectedEntityType();
    this.currentState = STATE.CREATE_NEW_CRITERION;
  }

  public selectEntityTreeName(data: SelectItem): void {
    this.selectedTreeNameItem = {...data} as SelectItem;
    this.orgTreeService
      .getTreeEntityTypes(this.selectedTreeNameItem?.id)
      .pipe(
        tap((response: EntityType[]) => {
          if (response.length !== 0) {
            this.entityTypeList = response.map((entityType: EntityType) => ({
              id: entityType.name,
              text: entityType.name,
              descriptionTransId: entityType.name,
            }));
            this.pagination.currentPage.number = 0;
            isEntityType(this.selectedZoneType) ? this.pageChanged() : this.refreshTableData();
          } else {
            this.criterionDataByTreeName = [];
            this.pagination.update(0);
          }
        }),
        switchMap(() => this.getEntityFormDescriptor()),
        tap((entityFormDescriptors) => this.entityFormDescriptors = entityFormDescriptors),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe();
  }

  public removeSelectedTreeName(): void {
    this.selectedTreeNameItem = null;
    this.selectedEntityType = null;
    this.selectedGroupZoneName = null;
    this.selectedZoneName = null;
    this.selectedZoneType = null;
    this.currentState = STATE.INITIAL_STATE;
    this.entityCriterionForm.reset();
  }

  public selectEntityType(data: SelectItem): void {
    this.removeSelectedEntityType();

    this.selectedEntityType = {...data} as SelectItem;
    this.populateGroupZoneNames();
  }

  public removeSelectedEntityType(): void {
    this.selectedEntityType = null;
    this.groupZoneNameTree = null;
    this.entityCriterionForm.patchValue({
      groupZoneName: null,
    });

    this.clearSelectedGroupZoneName();
  }

  public selectGroupZoneName(data: SelectedTreeItem): void {
    this.clearSelectedGroupZoneName();

    if (data?.id !== '') {
      this.selectedGroupZone = {...data} as SelectedTreeItem;
      this.selectedGroupZoneName = this.selectedGroupZone?.id;
      this.populateZoneNames();
    }
  }

  public selectZoneName(data: SelectedTreeItem): void {
    this.clearSelectedZoneName();

    if (data?.id !== '') {
      this.selectedZone = {...data} as SelectedTreeItem;
      this.selectedZoneName = this.selectedZone?.id;
      this.handleZoneType();
    }
  }

  public selectZoneValue(zone: SelectedTreeItem): void {
    this.selectedZoneTransId = zone.text;
  }

  public onFullAccessChecked(target: EventTarget): void {
    this.fullAccessChecked = (target as HTMLInputElement).checked;
    if (this.fullAccessChecked) {
      this.removeValidatorsAndResetCriterionForm();
    } else {
      this.setValidatorsAndUpdateCriterionForm();
    }
  }

  public pageChanged(): void {
    this.entityList$?.pipe(
      tap(entityList => this.entityList = entityList),
      switchMap(() => this.fetchRepPopulationCriterion()),
      takeUntil(this.componentDestroyed$)
    ).subscribe();
  }

  public createEntityAccessCriterion(): void {
    this.currentState = STATE.CREATE_NEW_CRITERION_IN_PROGRESS;
    this.addOrUpdateEntityAccessCriterion();
  }

  public updateEntityAccessCriterion(): void {
    this.currentState = STATE.UPDATE_CRITERION_IN_PROGRESS;
    this.addOrUpdateEntityAccessCriterion();
  }

  public handleDeleteCriterionConfirmation(): void {
    this.currentState = STATE.DELETE_CRITERION_IN_PROGRESS;

    this.userRightService
      .removeRepAccessCriterion(this.criterionToBeDeleted)
      .pipe(
        tap(() => {
          this.employeesUtilsService.accessCriterionsChanged$.next();
          this.discardDeleteCriterion();
          this.toastService.success(
            this.translate.instant(`SETTINGS.user_rights.access_criterion_deleted_successfully`)
          );
        }),
        switchMap(() => this.fetchRepPopulationCriterion()),
        catchError((errorResponse: HttpErrorResponse) => {
          const errorMessage = `${this.translate.instant(`SETTINGS.user_rights.access_criterion_deletion_failed`)}: ${
            errorResponse?.error?.message
          }`;
          this.toastService.error(errorMessage);
          return EMPTY;
        }),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe();
  }

  public updateCriterion(criterion: RepPopulationCriterion): void {
    this.showUpdateCriterionFormFields(this.entityCriterionForm);
    this.fullAccessChecked = criterion.groupZoneName == null;
    this.criterionToBeUpdated = criterion?.id;

    const selectedEntityType = {
      'id': criterion.entityType,
      'text': criterion.entityType,
      'isDisabled': false
    } as SelectItem;
    this.selectEntityType(selectedEntityType);

    const selectedGroupZoneName = {
      id: criterion.groupZoneName,
      previousId: null,
      text: criterion.groupZoneName
    } as SelectedTreeItem;
    this.selectGroupZoneName(selectedGroupZoneName);

    const selectedZoneName = {
      id: criterion.zoneName,
      previousId: null,
      text: criterion.zoneName
    } as SelectedTreeItem;
    this.selectZoneName(selectedZoneName);

    const criterionValue = criterion.value?.[criterion?.zoneName].value;

    if (this.fullAccessChecked) {
      this.removeValidatorsFromCriterionForm();
    } else {
      this.setValidatorsForCriterionForm();
    }

    if (isEntityType(this.selectedZoneType)) {
      const entityCriterionValue = criterionValue as string | number;
      this.entityList$ = this.createPopulationService.getEntityList(this.selectedZoneMetadata);
      this.entityList$.pipe(
        tap(entityList => {
          this.entityList = entityList;
          this.lazyValue = getEntityLazyValue(entityCriterionValue, this.entityList);
        }),
        takeUntil(this.componentDestroyed$)
      ).subscribe();
    }

    this.entityCriterionForm.patchValue({
      fullAccess: this.fullAccessChecked,
      entityType: criterion.entityType,
      groupZoneName: criterion.groupZoneName,
      zoneName: criterion.zoneName,
      accessMode: criterion.access,
      value: isBooleanType(this.selectedZoneType) ? !!criterionValue : criterionValue
    });
  }

  public checkCriterionValue(criterion: RepPopulationCriterion) {
    if (criterion.value) {
      return criterion.value[criterion.zoneName]?.type === FieldType.BOOLEAN;
    }
    return false;
  }

  public checkBooleanValue(criterion: RepPopulationCriterion): boolean {
    return JSON.parse(<string>criterion.value[criterion?.zoneName]?.value);
  }

  public getReadableName(criterion: RepPopulationCriterion) {
    if (criterion.value == null) {
      return '-';
    }

    const criterionValue = criterion.value[criterion?.zoneName];

    if (criterionValue?.type === FieldType.DATE) {
      return moment(criterionValue?.value.toString()).format(this.localizationService.dateFormat);
    }

    return (criterionValue?.transId) ? criterionValue?.transId : criterionValue?.value;
  }

  private handleZoneType(): void {
    const selectedZoneData = this.getSelectedZoneData();
    this.selectedZoneType = selectedZoneData?.types[0];

    this.selectedZoneMetadata = null;
    this.selectedZoneTransId = null;
    this.entityCriterionForm.patchValue({
      fullAccess: this.fullAccessChecked,
      value: null
    });

    const valueControl = this.entityCriterionForm.get('value');

    switch (this.selectedZoneType) {
      case FieldType.LIST:
        this.setValidatorsForCriterionForm();

        this.selectedZoneMetadata = this.manageAccessCriterionModalService.createListTree(selectedZoneData.metadatas?.items);
        break;

      case FieldType.TREE:
        this.setValidatorsForCriterionForm();

        this.selectedZoneMetadata = this.manageAccessCriterionModalService.createTree(selectedZoneData.metadatas?.items);
        break;

      case FieldType.ENTITY:
      case FieldType.ENTITY_DATA:
      case FieldType.ENTITY_PREFIXED:
        this.setValidatorsForCriterionForm();

        this.selectedZoneMetadata = selectedZoneData.metadatas?.rel;
        this.entityList$ = this.createPopulationService.getEntityList(this.selectedZoneMetadata);
        break;

      case FieldType.DATE:
        this.setValidatorsForCriterionForm();
        break;

      case FieldType.BOOLEAN:
        this.setValidatorsForCriterionForm();

        this.entityCriterionForm.patchValue({
          value: this.booleanValueChecked
        });

        break;

      default:
        if (valueControl) {
          const validators = [];
          validators.push(GpValidators.noWhitespaceValidator);
          validators.push(Validators.maxLength(this.MAX_ALLOWED_LENGTH));
          valueControl.setValidators(validators);
        }
        this.entityCriterionForm.updateValueAndValidity();
        break;
    }
  }

  private getSelectedZoneData(): FieldZone {
    const groupZoneNames = this.getGroupZoneNames();
    const zoneNames = this.getZoneNames(groupZoneNames);

    const data = zoneNames?.filter(fields => fields.name === this.selectedZoneName);
    return data?.length ? data[0] : null;
  }

  private fetchRepPopulationCriterion(): Observable<{ data: RepPopulationCriterion[]; total: number }> {
    this.currentState = STATE.TABLE_LOADING;
    const entityTypes = this.entityTypeList.map((entityType) => entityType.id);
    return this.userRightService
      .fetchRepPopulationCriterionByEntityTypes(
        entityTypes,
        this.pagination.currentPage.number,
        this.pagination.perPage
      )
      .pipe(
        tap((response) => {
          this.criterionDataByTreeName = response.data;
          this.pagination.update(response.total);
        }),
        catchError(() => of({data: [], total: 0})),
        finalize(() => {
          this.currentState = STATE.TABLE_LOADED;
        }),
        takeUntil(this.componentDestroyed$)
      );
  }

  private refreshTableData(): void {
    this.fetchRepPopulationCriterion().pipe(
      takeUntil(this.componentDestroyed$)
    ).subscribe();
  }

  private populateGroupZoneNames(): void {
    const groupZoneNames = this.getGroupZoneNames();
    this.groupZoneNameTree = this.createGroupZoneNameTree(groupZoneNames);
  }

  private populateZoneNames(): void {
    this.zoneNameTree = [];
    const groupZoneNames = this.getGroupZoneNames();
    const zoneNames = this.getZoneNames(groupZoneNames);
    this.zoneNameTree = this.createZoneNameTree(zoneNames);
  }

  private getEntityTreeNames(): void {
    this.orgTreeService
      .getTreeRoots(this.treeType, true)
      .pipe(
        tap((response: TreeRootResponse[]) => {
          this.entityTreeNameItems = response.map((treeRootResponse: TreeRootResponse) => ({
            id: treeRootResponse.name,
            text: treeRootResponse.name,
            descriptionTransId: treeRootResponse.transId,
          }));
        }),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe();
  }

  private getCriterionForUpdate(): RepPopulationCriterion | undefined {
    return this.criterionDataByTreeName.find((criterion) => criterion.id === this.criterionToBeUpdated);
  }

  private getGroupZoneNames(): FieldZone[] {
    const entityTypes = this.entityReportingFields?.filter(
      fields => fields.name === this.selectedTreeNameItem.id
    )[0]?.items;

    const groupZones = entityTypes?.filter(
      zone => zone.name === this.selectedEntityType.id
    )[0]?.items;

    groupZones?.forEach((zone: FieldZone) => {
      zone.items = zone.items?.filter(item => !item.types.includes(FieldType.FILE));
    });

    return groupZones?.filter((zone: FieldZone) => zone.items?.length > 0);
  }

  private getZoneNames(groupZoneNames: FieldZone[]): FieldZone[] {
    return groupZoneNames?.filter(
      fields => fields.name === this.selectedGroupZoneName
    )[0]?.items;
  }

  private createGroupZoneNameTree(groupZones: FieldZone[]): Array<TreeNode<TreeItem>> {
    const groupZoneNameTree: TreeNode<TreeItem>[] = [];

    groupZones?.forEach((groupZone: FieldZone) => {
      groupZoneNameTree.push({
        text: groupZone.name,
        value: groupZone.name,
        selectable: true,
        visible: true,
        children: [],
      });
    });

    return groupZoneNameTree;
  }

  private createZoneNameTree(zoneNames: FieldZone[]): Array<TreeNode<TreeItem>> {
    const zoneNameTree: TreeNode<TreeItem>[] = [];

    zoneNames?.forEach((zone: FieldZone) => {
      zoneNameTree.push({
        text: zone.name,
        value: zone.name,
        data: zone.metadatas,
        selectable: true,
        visible: true,
        children: []
      });
    });

    return zoneNameTree;
  }

  private addOrUpdateEntityAccessCriterion(): void {
    const formData = this.entityCriterionForm?.value;

    if (isDateType(this.selectedZoneType) || isNull(this.selectedZoneMetadata)) {
      this.selectedZoneTransId = formData.value;
    }

    if (isTreeType(this.selectedZoneType)) {
      this.selectedZoneTransId = this.getTransIdOfSelectedTreeItem(this.selectedZoneMetadata, formData.value);
    }

    if (isListType(this.selectedZoneType)) {
      this.selectedZoneTransId = this.selectedZoneMetadata.filter(item => item.value === formData.value)[0]?.text;
    }

    if (isEntityType(this.selectedZoneType)) {
      this.entityList$ = this.createPopulationService.getEntityList(this.selectedZoneMetadata);
      this.entityList$.pipe(
        tap(entityList => {
          this.entityList = entityList;
          this.lazyValue = getEntityLazyValue(formData.value, this.entityList);
          this.handleAddOrUpdateEntityAccessCriterion(formData);
        }),
        takeUntil(this.componentDestroyed$)
      ).subscribe();
    } else {
      this.handleAddOrUpdateEntityAccessCriterion(formData);
    }
  }

  private handleAddOrUpdateEntityAccessCriterion(formData: any): void {
    let msg = '';
    const fullAccessChecked = formData.fullAccess;
    const valueToNumeric = (isNumber(formData.value) ? formData.value : Number(formData.value));

    const payload: RepPopulationCriterion = {
      entityType: formData.entityType,
      access: formData.accessMode,
      groupZoneName: fullAccessChecked ? null : formData.groupZoneName,
      zoneName: fullAccessChecked ? null : formData.zoneName,
      value: fullAccessChecked
        ? null
        : {
          [formData.zoneName]: {
            type: this.selectedZoneType,
            value: (isNumberType(this.selectedZoneType) || isEntityType(this.selectedZoneType)) ?
              valueToNumeric : formData.value,
            transId: this.lazyValue ? this.lazyValue : this.selectedZoneTransId,
          },
        }
    };

    const addOrUpdateCriterion$ = this.isAddCriterionActive
      ? this.userRightService.createRepAccessCriterion(payload)
      : this.userRightService.updateRepAccessCriterion(this.criterionToBeUpdated, payload);

    addOrUpdateCriterion$
      .pipe(
        tap(() => {
          this.employeesUtilsService.accessCriterionsChanged$.next();
          if (this.isAddCriterionActive) {
            this.showAddCriterionFormFields();
            msg = `SETTINGS.user_rights.access_criterion_added_successfully`;
          } else {
            this.showUpdateCriterionFormFields(this.entityCriterionForm);
            msg = `SETTINGS.user_rights.access_criterion_modification_successfully`;
          }
          this.toastService.success(this.translate.instant(msg));
          this.refreshTableData();
        }),
        catchError((errorResponse: HttpErrorResponse) => {
          if (this.isAddCriterionActive) {
            this.currentState = STATE.CREATE_NEW_CRITERION;
            msg = `SETTINGS.user_rights.access_criterion_creation_failed`;
          } else {
            this.currentState = STATE.UPDATE_CRITERION;
            msg = `SETTINGS.user_rights.access_criterion_modification_failed`;
          }
          const errorMessage = `${this.translate.instant(msg)}: ${
            errorResponse?.error?.message
          }`;
          this.toastService.error(errorMessage);
          return EMPTY;
        }),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe();
  }

  private getTransIdOfSelectedTreeItem(fieldListTree: Array<TreeNode<TreeItem>>, selectedValue: string): string {
    let selectedItem;
    let selectedItemFound = false;
    const getSelectedItemDetail = (fieldTree: Array<TreeNode<TreeItem>>, value: string) => {
      for (const item of fieldTree) {
        if (selectedItemFound) {
          break;
        }
        const hasChildren = item?.children?.length > 0;
        if (hasChildren) {
          getSelectedItemDetail(item.children, value);
        } else if (item.value === value) {
          selectedItem = item;
          selectedItemFound = true;
        }
      }
      return selectedItem;
    };

    return getSelectedItemDetail(fieldListTree, selectedValue)?.text;
  }

  private reviveCriterionUpdateForm(criterionForUpdate: RepPopulationCriterion | undefined): void {
    if (criterionForUpdate) {
      this.entityCriterionForm.patchValue({
        groupZoneName: criterionForUpdate.groupZoneName,
        zoneName: criterionForUpdate.zoneName,
        value: criterionForUpdate?.value?.[criterionForUpdate?.zoneName]?.transId || '',
      });

      const selectedZoneData = this.getSelectedZoneData();
      this.selectedZoneType = selectedZoneData?.types[0];
    }
  }

  private removeValidatorsFromCriterionForm(): void {
    this.dynamicValidationFormControls.forEach((controlName) => {
      this.entityCriterionForm.get(controlName).clearValidators();
    });
    this.entityCriterionForm.updateValueAndValidity();
  }

  private setValidatorsForCriterionForm(): void {
    this.dynamicValidationFormControls.forEach((controlName) => {
      if (controlName === 'value') {
        if (isBooleanType(this.selectedZoneType)) {
          this.entityCriterionForm.get(controlName).clearValidators();
          this.entityCriterionForm
            .get(controlName)
            .addValidators([
              Validators.required
            ]);
        } else if (isListType(this.selectedZoneType) || isTreeType(this.selectedZoneType) || isEntityType(this.selectedZoneType)) {
          this.entityCriterionForm
            .get(controlName)
            .addValidators([
              Validators.required,
            ]);
        } else {
          this.entityCriterionForm.get(controlName).clearValidators();
          this.entityCriterionForm
            .get(controlName)
            .addValidators([
              Validators.required,
              GpValidators.noWhitespaceValidator,
              Validators.maxLength(this.MAX_ALLOWED_LENGTH),
            ]);
        }
      } else {
        this.entityCriterionForm
          .get(controlName)
          .addValidators([
            Validators.required,
            GpValidators.noWhitespaceValidator,
            Validators.maxLength(this.MAX_ALLOWED_LENGTH),
          ]);
      }
    });
    this.entityCriterionForm.updateValueAndValidity();
  }

  private removeValidatorsAndResetCriterionForm(): void {
    this.removeValidatorsFromCriterionForm();
    this.resetCriterionFormValues();
  }

  private setValidatorsAndUpdateCriterionForm(): void {
    this.setValidatorsForCriterionForm();
    if (this.criterionToBeUpdated !== 0) {
      const criterionForUpdate = this.getCriterionForUpdate();
      this.reviveCriterionUpdateForm(criterionForUpdate);
    } else {
      this.resetCriterionFormValues();
    }
  }

  private resetCriterionFormValues(): void {
    this.entityCriterionForm.patchValue({
      fullAccess: false,
      groupZoneName: null,
      zoneName: null,
      value: null,
    });

    this.selectedZoneType = null;
  }

  private clearSelectedGroupZoneName(): void {
    this.selectedGroupZoneName = null;

    this.zoneNameTree = null;
    this.entityCriterionForm.patchValue({
      zoneName: null,
    });

    this.clearSelectedZoneName();
  }

  private clearSelectedZoneName(): void {
    this.selectedZoneName = null;
    this.selectedZoneType = null;
    this.entityCriterionForm.patchValue({
      value: null,
    });
  }
}
